import {
  AccountSortOption,
  AccountType,
} from '@rematter/frontend-lib-api-client';

import {
  createQueryAtomFactory,
  QueryAtomResult,
} from '~/shared/providers/api/createQueryAtomFactory';

export type AccountRecord = QueryAtomResult<
  typeof accountsQueryAtom
>['accounts'][number];

export type AddressRecord = AccountRecord['addresses'][number];

export type ContactRecord =
  AccountRecord['addresses'][number]['contacts'][number];

const destructFullName = (fullName: string) => {
  const [firstName, ...lastNames] = fullName.trim().split(' ');
  return { firstName, lastName: lastNames.join(' ') };
};

export const accountsQueryAtom = createQueryAtomFactory(
  'crm.accounts',
  (params: {
    accountIds?: string[];
    searchTerm?: string;
    offset?: number;
    limit?: number;
    sorting?: AccountSortOption;
    accountTypes?: AccountType[];
  }) => ({
    accounts: {
      __args: {
        filter: {
          searchTerm: params.searchTerm,
          ids: params.accountIds,
          accountType: params.accountTypes,
        },
        pagination: {
          offset: params.offset ?? 0,
          limit: params.limit ?? 10,
        },
        sorting: params.sorting ? [params.sorting] : undefined,
      },
      page: {
        total: true,
      },
      entries: {
        id: true,
        name: true,
        type: true,
        email: true,
        phone: true,
        website: true,
        notes: true,
        relationship: {
          isCustomer: true,
          isSupplier: true,
          isCarrier: true,
        },
        timestamps: {
          createdAt: true,
          updatedAt: true,
        },
        accountManagerIds: true,
        accountManagers: {
          id: true,
          firstName: true,
          lastName: true,
        },
        canBeInvited: true,
        defaultPurchasePaymentTermId: true,
        defaultSalesPaymentTermId: true,
        defaultPurchasePaymentTerm: {
          id: true,
          name: true,
          code: true,
        },
        defaultSalesPaymentTerm: {
          id: true,
          name: true,
          code: true,
        },
        addresses: {
          id: true,
          name: true,
          address: {
            addressType: true,
            streetAddress: true,
            streetAddress2: true,
            city: true,
            state: true,
            postalCode: true,
            country: true,
            isMailingAddress: true,
            coordinates: {
              latitude: true,
              longitude: true,
            },
          },
          contacts: {
            id: true,
            fullName: true,
            addressAs: true,
            email: true,
            phone: true,
            prefix: true,
            role: true,
            department: true,
            notes: true,
            isPrimary: true,
            timestamps: {
              createdAt: true,
              updatedAt: true,
            },
          },
        },
      },
    },
  }),
  {
    select: (data) => ({
      accounts:
        data?.accounts.entries.map((entry) => ({
          id: entry.id,
          name: entry.name,
          type: entry.type,
          email: entry.email,
          phone: entry.phone,
          website: entry.website,
          relationship: {
            isCustomer: !!entry.relationship?.isCustomer,
            isSupplier: !!entry.relationship?.isSupplier,
            isCarrier: !!entry.relationship?.isCarrier,
          },
          addresses: entry.addresses.map((addr) => ({
            id: addr.id,
            name: addr.name,
            ...addr.address,
            contacts: (addr.contacts ?? []).map((c) => ({
              ...c,
              ...destructFullName(c.fullName),
            })),
          })),
          notes: entry.notes,
          timestamps: entry.timestamps,
          accountManagerIds: entry.accountManagerIds,
          accountManagers: entry.accountManagers,
          canBeInvited: entry.canBeInvited,
          defaultPurchasePaymentTermId: entry.defaultPurchasePaymentTermId,
          defaultSalesPaymentTermId: entry.defaultSalesPaymentTermId,
          defaultPurchasePaymentTerm: entry.defaultPurchasePaymentTerm,
          defaultSalesPaymentTerm: entry.defaultSalesPaymentTerm,
        })) ?? [],
      total: data?.accounts.page.total,
    }),
  },
);
